import { useState, useEffect, useRef, useCallback } from 'react';
import aiService from '../../../services/aiService';
import { Model, AIConnectionStatus } from '../types';
import {
  DEFAULT_PROVIDER,
  DEFAULT_GEMINI_MODEL,
  FALLBACK_MODEL,
  FALLBACK_MODEL_STORAGE_KEY,
  GEMINI_MODEL_STORAGE_KEY,
  LEGACY_FALLBACK_MODEL,
} from '../../../config';
import type { GeminiCatalog, GeminiCatalogSource, GeminiCategory } from '../../../api/catalog/geminiModels';

// localStorage can throw (privacy mode) — model choice persistence is best-effort.
const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeStorage = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* best-effort */
  }
};
const removeStorage = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* best-effort */
  }
};

/**
 * Persists the Gemini model choice only when it DEVIATES from DEFAULT_GEMINI_MODEL;
 * choosing the default clears the key. So a saved value always means "the user
 * picked something else", and users on the default follow future default changes
 * (2026-09-30: 2.5-flash → 3.1-flash-lite) instead of being pinned to the old one.
 */
const persistGeminiChoice = (model: string) => {
  if (model === DEFAULT_GEMINI_MODEL) removeStorage(GEMINI_MODEL_STORAGE_KEY);
  else writeStorage(GEMINI_MODEL_STORAGE_KEY, model);
};

/**
 * Gemini model to select for a given list: the saved choice (localStorage) when it is
 * still listed, else DEFAULT_GEMINI_MODEL, else the list head. Used on catalog load
 * and when switching back to the Gemini provider (so the saved choice survives).
 */
export const pickGeminiModel = (models: Array<{ id: string }>): string => {
  if (models.length === 0) return '';
  const saved = readStorage(GEMINI_MODEL_STORAGE_KEY);
  if (saved && models.some((m) => m.id === saved)) return saved;
  // Prefer the configured default over the list head: the newest Flash sits
  // first there, and 3.5+ Flash with thinking can burn its whole token budget
  // and return empty content (QA DEV 2026-06-10, see src/config.ts).
  return models.some((m) => m.id === DEFAULT_GEMINI_MODEL) ? DEFAULT_GEMINI_MODEL : models[0].id;
};

/**
 * Provider/model selection state extracted from useAIIntegration (wave 2.4a).
 * Owns: connection status, model lists for both providers, the selected
 * provider/model pair and the persisted fallback model.
 */
export const useAIProvider = () => {
  const [aiConnectionStatus, setAiConnectionStatus] = useState<AIConnectionStatus>({ openrouter: false, gemini: false });
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [availableModels, setAvailableModels] = useState<Model[]>([]);
  const [geminiModels, setGeminiModels] = useState<Model[]>([]);
  const [geminiCategories, setGeminiCategories] = useState<GeminiCategory[]>([]);
  const [geminiCatalogSource, setGeminiCatalogSource] = useState<GeminiCatalogSource | null>(null);
  // Short user-facing info, e.g. "saved model no longer available — reverted to default".
  const [modelNotice, setModelNotice] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState('');
  // Default to Gemini — OpenRouter's free models are persistently rate-limited (429)
  const [selectedProvider, setSelectedProvider] = useState<'openrouter' | 'gemini'>(DEFAULT_PROVIDER);
  const [fallbackModel, setFallbackModel] = useState(FALLBACK_MODEL);
  const [loadingModels, setLoadingModels] = useState(false);
  const fallbackModelRef = useRef(fallbackModel);
  fallbackModelRef.current = fallbackModel;

  /**
   * Applies a freshly loaded Gemini catalog: stores models/categories, restores the
   * saved model choice (localStorage) and — only when the catalog is authoritative
   * (source api/cache, not the static offline list) — replaces saved choices that
   * vanished from it with the defaults, with a short notice.
   */
  const applyGeminiCatalog = useCallback((catalog: GeminiCatalog) => {
    const models = catalog.models as Model[];
    setGeminiModels(models);
    setGeminiCategories(catalog.categories ?? []);
    setGeminiCatalogSource(catalog.source);
    if (models.length === 0) return;

    const ids = new Set(models.map((m) => m.id));
    const authoritative = catalog.source !== 'fallback';
    const defaultId = ids.has(DEFAULT_GEMINI_MODEL) ? DEFAULT_GEMINI_MODEL : models[0].id;
    const notices: string[] = [];

    const saved = readStorage(GEMINI_MODEL_STORAGE_KEY);
    if (saved && !ids.has(saved) && authoritative) {
      notices.push(`Zapisany model „${saved}” nie jest już dostępny — przywrócono domyślny (${defaultId}).`);
      persistGeminiChoice(defaultId);
    }
    const initial = pickGeminiModel(models);
    setSelectedModel((prev) => prev || initial);

    const currentFallback = fallbackModelRef.current;
    if (authoritative && currentFallback && !ids.has(currentFallback)) {
      const nextFallback = ids.has(FALLBACK_MODEL) ? FALLBACK_MODEL : defaultId;
      notices.push(`Model zapasowy „${currentFallback}” nie jest już dostępny — ustawiono ${nextFallback}.`);
      setFallbackModel(nextFallback);
    }
    setModelNotice(notices.length > 0 ? notices.join(' ') : null);
  }, []);

  // Auto-check AI connection on component mount
  const connectionCheckRan = useRef(false);
  useEffect(() => {
    if (connectionCheckRan.current) return;
    connectionCheckRan.current = true;

    const checkConnection = async () => {
      console.log('🔌 Auto-checking AI connection on startup...');
      setIsTestingConnection(true);

      // Start fetching models in parallel immediately
      const modelLoads: Promise<void>[] = [];
      modelLoads.push(
        aiService.getFreeModels().then(models => {
          setAvailableModels(models);
        }).catch(err => {
          console.error('❌ Failed to fetch OpenRouter models:', err);
        })
      );
      modelLoads.push(
        // Default provider is Gemini, so the initial model comes from the Gemini catalog.
        aiService.getGeminiCatalog().then(applyGeminiCatalog).catch(err => {
          console.error('❌ Failed to fetch Gemini models:', err);
        })
      );

      try {
        const status = await aiService.testConnection();
        console.log('✅ AI Connection status:', status);
        setAiConnectionStatus(status);
      } catch (error: any) {
        console.error('❌ Błąd automatycznego sprawdzania połączenia:', error);
        setAiConnectionStatus({ openrouter: false, gemini: false });
      } finally {
        await Promise.allSettled(modelLoads);
        setIsTestingConnection(false);
      }
    };

    checkConnection();
  }, [applyGeminiCatalog]);

  // Load saved fallback model. The old default (gemini-3-flash-preview) was written
  // to localStorage for every user on mount, so it is not a real choice — migrate it.
  useEffect(() => {
    const savedFallback = readStorage(FALLBACK_MODEL_STORAGE_KEY);
    if (savedFallback && savedFallback !== LEGACY_FALLBACK_MODEL) {
      fallbackModelRef.current = savedFallback;
      setFallbackModel(savedFallback);
    }
  }, []);

  // Save fallback model to localStorage when changed
  useEffect(() => {
    writeStorage(FALLBACK_MODEL_STORAGE_KEY, fallbackModel);
  }, [fallbackModel]);

  // Remember the chosen Gemini model — only ids confirmed by an authoritative
  // catalog (the offline static list must not overwrite a saved choice).
  useEffect(() => {
    if (selectedProvider !== 'gemini' || !selectedModel) return;
    if (!geminiCatalogSource || geminiCatalogSource === 'fallback') return;
    if (!geminiModels.some((m) => m.id === selectedModel)) return;
    persistGeminiChoice(selectedModel);
  }, [selectedProvider, selectedModel, geminiModels, geminiCatalogSource]);

  const testAIConnection = async () => {
    setIsTestingConnection(true);
    try {
      const status = await aiService.testConnection();
      setAiConnectionStatus(status);
    } catch (error) {
      console.error('Błąd testowania połączenia:', error);
      setAiConnectionStatus({ openrouter: false, gemini: false });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const loadFreeModels = async () => {
    setLoadingModels(true);
    try {
      const models = await aiService.getFreeModels();
      setAvailableModels(models);
      if (models.length > 0) {
        setSelectedModel(models[0].id);
      }
    } catch (error) {
      console.error('Błąd ładowania modeli:', error);
    } finally {
      setLoadingModels(false);
    }
  };

  const loadGeminiModels = async () => {
    setLoadingModels(true);
    try {
      applyGeminiCatalog(await aiService.getGeminiCatalog());
    } catch (error) {
      console.error('Błąd ładowania modeli Gemini:', error);
    } finally {
      setLoadingModels(false);
    }
  };

  return {
    aiConnectionStatus,
    isTestingConnection,
    availableModels,
    geminiModels,
    geminiCategories,
    geminiCatalogSource,
    modelNotice,
    selectedModel,
    selectedProvider,
    fallbackModel,
    loadingModels,
    setSelectedModel,
    setSelectedProvider,
    setFallbackModel,
    testAIConnection,
    loadFreeModels,
    loadGeminiModels
  };
};
